import { ChangeDetectionStrategy, Component, inject, signal, ChangeDetectorRef } from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { FIELD_CONTROL, FIELD_CONFIG } from './field-tokens';
import { UiOrchestratorService } from '../../../core/services/ui-orchestrator.service';

@Component({
  selector: 'app-field-file-upload',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
  template: `
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center gap-2 flex-wrap">
        <label class="p-button p-button-outlined p-button-secondary p-button-sm
                      cursor-pointer inline-flex items-center gap-2 max-w-full"
               [class.p-disabled]="formControl.disabled">
          <i [class]="fileName() ? 'pi pi-file text-primary-500' : 'pi pi-paperclip text-xs'"></i>
          <span class="text-sm truncate max-w-[280px]" [title]="fileName() ? 'Uploaded: ' + fileName() : ''">
            {{ fileName() ? fileName() : (config.placeholder || 'Choose file') }}
          </span>
          <input type="file" [accept]="acceptExtensions" class="hidden"
                 [disabled]="formControl.disabled" (change)="onFileChange($event)" />
        </label>
        @if (fileName()) {
          <button type="button" class="text-surface-400 hover:text-red-500 transition-colors p-1"
                  [disabled]="formControl.disabled"
                  (click)="removeFile()"
                  title="Remove file">
            <i class="pi pi-times text-xs"></i>
          </button>
        }
      </div>
      @if (fileName()) {
        <span class="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
          <i class="pi pi-check text-[10px]"></i>
          Uploaded: {{ fileName() }}
        </span>
      }
    </div>
  `
})
export class FileUploadFieldComponent {
  readonly config  = inject(FIELD_CONFIG);
  readonly control = inject(FIELD_CONTROL);
  readonly cdr     = inject(ChangeDetectorRef);
  private readonly ui = inject(UiOrchestratorService, { optional: true });
  get formControl(): FormControl { return this.control as FormControl; }

  get acceptExtensions(): string {
    return this.config.accept || '.pdf,.doc,.docx';
  }

  // Navigating away from this step and back destroys and recreates this component (each
  // step's fields only exist in the DOM while active), which previously reset this signal to
  // null even though the FormControl's value — held by the parent FormGroup, not this
  // component — was still intact. Looked exactly like "the file disappeared" even though it
  // hadn't. The original filename can't be recovered from a bare base64 data URL though, so
  // this falls back to a generic label instead of silently showing nothing.
  private getInitialFileName(): string | null {
    const val = this.formControl.value;
    if (!val) return null;
    if (typeof val === 'object' && val.file_name) return val.file_name;
    if (typeof val === 'string') {
      if (!val.startsWith('data:')) {
        const name = val.split('?')[0].split('/').pop();
        if (name && name.includes('.')) return name;
      }
      return 'File attached';
    }
    return null;
  }

  fileName = signal<string | null>(this.getInitialFileName());

  private isFileValid(file: File): boolean {
    const rawAccept = this.acceptExtensions;
    if (!rawAccept) return true;

    const allowedTokens = rawAccept
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(Boolean);

    if (allowedTokens.length === 0) return true;

    const fileName = file.name.toLowerCase();
    const fileType = (file.type || '').toLowerCase();

    return allowedTokens.some(token => {
      if (token.startsWith('.')) {
        return fileName.endsWith(token);
      }
      if (token.endsWith('/*')) {
        const prefix = token.slice(0, -1);
        return fileType.startsWith(prefix);
      }
      return fileType === token;
    });
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (!this.isFileValid(file)) {
      this.fileName.set(null);
      this.formControl.setValue(null);
      this.formControl.markAsDirty();
      this.formControl.markAsTouched();
      this.formControl.setErrors({ invalidFileFormat: true });
      this.ui?.toast({
        severity: 'error',
        summary: 'Invalid File Type',
        detail: 'Invalid file format. Please upload a PDF or Word document (.pdf, .doc, .docx).'
      });
      this.cdr.markForCheck();
      return;
    }

    this.fileName.set(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      this.formControl.setValue({
        file_name: file.name,
        file_data: reader.result as string
      });
      this.formControl.markAsDirty();
      this.formControl.markAsTouched();
      this.formControl.setErrors(null);
      this.formControl.updateValueAndValidity();
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      this.fileName.set(null);
      this.formControl.setValue(null);
      this.formControl.markAsDirty();
      this.formControl.markAsTouched();
      this.formControl.setErrors({ serverError: 'Could not read the selected file.' });
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  removeFile(): void {
    this.fileName.set(null);
    this.formControl.setValue(null);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.formControl.setErrors(null);
    this.formControl.updateValueAndValidity();
    this.cdr.markForCheck();
  }
}
